import type { SimpleSearchQuery } from '@/shared/ui/search/SimpleSearch/types/search.types';
import type { SearchQuery } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import type { OrderSearchQuery, OrderSearchFilterGroup } from '@/entities/order';
import type { FilterGroup } from '@/shared/ui/search/AdvancedSearch/types/filter-group.type';
import type { Filter } from '@/shared/ui/search/AdvancedSearch/types/filter.types';

/** 구 필드명 → 서버 `OrderSearchField` JSON 값 (탭/URL에 남은 값 호환) */
const LEGACY_ORDER_SEARCH_FIELD: Record<string, string> = {
  departure: 'departureLocation',
  arrival: 'arrivalLocation',
  orderer: 'ordererMisoCustomerId',
};

function canonicalOrderSearchFieldName(fieldName: string): string {
  return LEGACY_ORDER_SEARCH_FIELD[fieldName] ?? fieldName;
}

function normalizeLegacyOrderSearchQuery(
  q: SimpleSearchQuery | SearchQuery
): SimpleSearchQuery | SearchQuery {
  if ('filterGroups' in q && Array.isArray(q.filterGroups)) {
    return {
      ...q,
      filterGroups: q.filterGroups.map((g: FilterGroup) => ({
        ...g,
        filters: g.filters.map((f: Filter) => ({
          ...f,
          fieldName: canonicalOrderSearchFieldName(f.fieldName),
        })),
      })),
    };
  }
  if ('fieldName' in q && typeof q.fieldName === 'string' && q.fieldName) {
    return { ...q, fieldName: canonicalOrderSearchFieldName(q.fieldName) };
  }
  return q;
}

/** 주문 검색 탭 params 키 (간단검색/고급검색 컨텍스트 저장) */
export const ORDER_SEARCH_PARAM_KEYS = {
  searchMode: 'searchMode',
  searchQuery: 'searchQuery',
} as const;

export type OrderSearchParams = {
  searchMode?: 'simple' | 'advanced';
  searchQuery?: string; // JSON string
};

export function parseOrderSearchParams(
  params: Record<string, unknown> | undefined
): { searchMode?: 'simple' | 'advanced'; searchQuery?: SimpleSearchQuery | SearchQuery } | null {
  if (!params) return null;
  const mode = params[ORDER_SEARCH_PARAM_KEYS.searchMode];
  const queryStr = params[ORDER_SEARCH_PARAM_KEYS.searchQuery];
  if (!mode && !queryStr) return null;
  const result: {
    searchMode?: 'simple' | 'advanced';
    searchQuery?: SimpleSearchQuery | SearchQuery;
  } = {};
  if (mode === 'simple' || mode === 'advanced') result.searchMode = mode;
  if (typeof queryStr === 'string' && queryStr.trim()) {
    try {
      const parsed = JSON.parse(queryStr) as SimpleSearchQuery | SearchQuery;
      if (parsed && typeof parsed === 'object') {
        result.searchQuery = normalizeLegacyOrderSearchQuery(parsed);
      }
    } catch {
      /* ignore */
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

export function serializeOrderSearchParams(
  searchMode: 'simple' | 'advanced',
  searchQuery: SimpleSearchQuery | SearchQuery
): OrderSearchParams {
  return {
    [ORDER_SEARCH_PARAM_KEYS.searchMode]: searchMode,
    [ORDER_SEARCH_PARAM_KEYS.searchQuery]: JSON.stringify(searchQuery),
  };
}

function generateId(): string {
  return `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function defaultQuery(pageSize: number, cursor?: string): OrderSearchQuery {
  return {
    filterGroups: [],
    sortBy: 'orderedAt',
    sortOrder: 'DESC',
    pageSize: pageSize,
    cursor: cursor,
  };
}

/**
 * 실시간 주문조회용 쿼리 생성
 * 버튼 클릭 시점 이후 접수된 주문만 검색 (orderedAt >= startTime)
 * 시계열 내림차순으로 정렬
 */
export function createRealtimeOrderQuery(
  startTime: Date,
  cursor?: string | null
): OrderSearchQuery {
  const query: OrderSearchQuery = {
    filterGroups: [
      {
        logic: 'AND' as const,
        filters: [
          {
            field: 'orderedAt',
            operator: 'RANGE_GTE',
            value: startTime.toISOString(),
            values: null,
          },
        ],
      },
    ],
    sortBy: 'orderedAt',
    sortOrder: 'DESC',
    pageSize: 100,
  };
  if (cursor) query.cursor = cursor;
  return query;
}

export function convertSimpleToAdvanced(
  simpleQuery: SimpleSearchQuery,
  defaultFieldName: string = 'misoOrderId'
): SearchQuery {
  const fieldName = canonicalOrderSearchFieldName(simpleQuery.fieldName || defaultFieldName);

  const filter: Filter = {
    id: generateId(),
    fieldName,
    operator: 'CONTAINS',
    value: simpleQuery.value,
  };

  const filterGroup: FilterGroup = {
    id: generateId(),
    logic: 'AND',
    filters: [filter],
  };

  return {
    filterGroups: [filterGroup],
    sort: {
      field: 'orderedAt',
      direction: 'DESC',
    },
    pagination: {
      pageSize: 20,
    },
  };
}

/**
 * SearchQuery를 SimpleSearchQuery로 변환
 * 첫 번째 필터 그룹의 첫 번째 필터를 간단검색으로 변환
 * CONTAINS 또는 EQUALS 연산자이고 문자열 값인 경우 변환 가능
 */
export function convertAdvancedToSimple(advancedQuery: SearchQuery): SimpleSearchQuery | null {
  // 필터 그룹이 없으면 null 반환
  if (advancedQuery.filterGroups.length === 0) {
    return null;
  }

  // 첫 번째 필터 그룹의 첫 번째 필터 찾기
  const firstGroup = advancedQuery.filterGroups[0];
  if (firstGroup.filters.length === 0) {
    return null;
  }

  const firstFilter = firstGroup.filters[0];

  // CONTAINS 또는 EQUALS 연산자이고 문자열 값인 경우 변환
  if (
    (firstFilter.operator === 'CONTAINS' || firstFilter.operator === 'EQUALS') &&
    typeof firstFilter.value === 'string' &&
    firstFilter.value.trim() !== ''
  ) {
    // 간단검색에서 지원하는 필드인지 확인
    const supportedFields = ['misoOrderId', 'ordererPhone', 'departureLocation', 'arrivalLocation'];
    const canonicalField = canonicalOrderSearchFieldName(firstFilter.fieldName);
    const fieldName = supportedFields.includes(canonicalField) ? canonicalField : 'misoOrderId'; // 지원하지 않는 필드는 기본값으로

    return {
      fieldName,
      value: firstFilter.value,
    };
  }

  return null; // 변환 불가능
}

/**
 * value를 서버가 기대하는 문자열 형식으로 변환
 * 서버는 value를 String? 타입으로만 받음
 */
function convertFilterValue(value: any): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'boolean') {
    return value.toString();
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (typeof value === 'string') {
    return value;
  }

  // 기타 타입은 문자열로 변환
  return String(value);
}

/**
 * values 배열을 서버가 기대하는 문자열 배열로 변환
 */
function convertFilterValues(values: readonly (string | number)[]): string[] {
  return values.map((v) => (typeof v === 'string' ? v : String(v)));
}

/**
 * 고급검색 orderedAt(datetime-local) → 서버 `OffsetDateTime.parse` 호환 (로컬 입력을 KST 오프셋으로 고정)
 */
function normalizeOrderedAtFilterValue(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00+09:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}+09:00`;
  }
  return trimmed;
}

/** 값이 비어있으면 false (빈 필터는 조건 없이 조회하기 위해 제외) */
function hasFilterValue(value: Filter['value']): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return !(Array.isArray(value) && value.length === 0);
}

/**
 * SearchQuery를 OrderSearchQuery로 변환
 * 서버 스펙에 맞게 변환:
 * - FilterGroup에서 id 제거
 * - Filter에서 id 제거하고 fieldName을 field로 변환
 * - value를 String으로 변환 (서버는 String?만 받음)
 * - values를 String[]로 변환 (서버는 List<String>만 받음)
 * - 값이 비어있는 필터는 제외 (조건 없이 조회)
 */
export function convertSearchQueryToOrderQuery(
  searchQuery: SearchQuery,
  cursor?: string
): OrderSearchQuery {
  // 값이 비어있는 필터 제외 → 빈 그룹 제외
  const validGroups = searchQuery.filterGroups
    .map((group) => ({
      ...group,
      filters: group.filters.filter((f) => hasFilterValue(f.value)),
    }))
    .filter((group) => group.filters.length > 0);

  // 서버 스펙에 맞게 filterGroups 변환 (id 제거, fieldName -> field, value -> String)
  const filterGroups: OrderSearchFilterGroup[] = validGroups.map((group) => ({
    logic: group.logic,
    filters: group.filters.map((filter) => {
      const convertedFilter: any = {
        field: canonicalOrderSearchFieldName(filter.fieldName), // fieldName을 field로 변환 (레거시 호환)
        operator: filter.operator,
      };

      // value 또는 values 설정 (서버 스펙에 따라)
      // 서버는 value를 String?, values를 List<String>?로만 받음
      // values 필드를 명시적으로 포함해야 할 수 있음
      if (Array.isArray(filter.value)) {
        convertedFilter.values = convertFilterValues(filter.value);
        convertedFilter.value = null; // values가 있으면 value는 null
      } else if (filter.value !== undefined && filter.value !== null) {
        const stringValue =
          filter.fieldName === 'orderedAt' && typeof filter.value === 'string'
            ? normalizeOrderedAtFilterValue(filter.value)
            : convertFilterValue(filter.value);
        if (stringValue !== undefined) {
          convertedFilter.value = stringValue;
        }
        convertedFilter.values = null; // value가 있으면 values는 null
      } else {
        // value와 values가 모두 없으면 명시적으로 null 설정
        convertedFilter.value = null;
        convertedFilter.values = null;
      }

      return convertedFilter;
    }),
  }));

  const result: OrderSearchQuery = {
    filterGroups,
    sortBy: (searchQuery.sort?.field as 'orderedAt' | 'moveDate' | 'id') || 'orderedAt',
    sortOrder: (searchQuery.sort?.direction.toUpperCase() as 'ASC' | 'DESC') || 'DESC',
    pageSize: searchQuery.pagination?.pageSize || 20,
  };

  // cursor가 있을 때만 포함
  if (cursor) {
    result.cursor = cursor;
  }

  return result;
}

/**
 * SimpleSearchQuery를 OrderSearchQuery로 변환
 */
export function convertSimpleToOrderQuery(
  simpleQuery: SimpleSearchQuery,
  cursor?: string,
  defaultFieldName: string = 'misoOrderId'
): OrderSearchQuery {
  const searchQuery = convertSimpleToAdvanced(simpleQuery, defaultFieldName);
  return convertSearchQueryToOrderQuery(searchQuery, cursor);
}
